<template>
  <div class="article-list p-3" :class="['layout-' + layoutMode]" :style="{ height: height ? height + 'px' : '100%', width: width ? width + 'px' : '100%' }" ref="articleListRef">
    <div class="article-header">
      <h6>
        <a href="javascript:void(0)" @click="$emit('toggle-sidebar')">
          {{ feedStore.selectedNode ? feedStore.selectedNode.siteName : '' }}
        </a>
        - 文章列表
      </h6>
      <div class="header-actions">
        <button class="action-button" @click="toggleSelectAll" title="全选">
          <span class="action-icon">☑</span>
          <span>全选</span>
        </button>
        <button class="action-button" @click="exportSelected" :disabled="!hasSelected" title="导出">
          <span class="action-icon">📤</span>
          <span>导出</span>
        </button>
        <button class="action-button" @click="$router.push('/search')" title="搜索">
          <span class="action-icon">🔍</span>
          <span class="action-text">搜索</span>
        </button>
        <button class="action-button" @click="$router.push('/card-view')" title="卡片视图">
          <span class="action-icon">📋</span>
          <span>卡片视图</span>
        </button>
        <a class="action-button setup-link" @click="redirectToSetup" title="偏好设置">
          <span class="action-icon">⚙</span>
          <span>偏好设置</span>
        </a>
        <button class="action-button layout-toggle-btn" @click="$emit('toggle-layout')" :title="layoutMode === 'horizontal' ? '切换为上下布局' : '切换为宽屏左右布局'">
          <span class="action-icon">{{ layoutMode === 'horizontal' ? '⏸' : '⏹' }}</span>
          <span>{{ layoutMode === 'horizontal' ? '上下布局' : '宽屏布局' }}</span>
        </button>
        <button class="action-button theme-toggle-btn" @click="themeStore.toggleTheme" :title="themeStore.currentTheme === 'dark' ? '切换亮色' : '切换暗色'">
          <span class="action-icon">{{ themeStore.currentTheme === 'dark' ? '☀️' : '🌙' }}</span>
          <span>{{ themeStore.currentTheme === 'dark' ? '亮色' : '暗色' }}</span>
        </button>
      </div>
    </div>

    <ul class="list-group">
      <li
        v-for="article in feedStore.articles" 
        :key="article.idString" 
        class="list-article-item"
        :class="{ 'read': article.isRead, 'active': article.idString === currentArticleId }"
        @click="onArticleClick(article)">
        <input type="checkbox" class="checkbox" :checked="article.isSelected" @change.stop="toggleSelectArticle(article)" />
        <span class="article-title">{{ article.title }}</span>
        <div class="item-controls">
          <img 
            src="@/assets/images/star.svg" 
            class="icon bookmark-icon" 
            alt="bookmark icon"
            :style="{ opacity: article.isBookmarked ? '1' : '0.5' }"
            @click.stop="toggleBookmark(article)"
            :title="article.isBookmarked ? '取消收藏' : '收藏文章'"
          /> 
          <img 
            src="@/assets/images/rss.svg" 
            class="icon rss-icon" 
            alt="rss icon"
            @click.stop="markAsRead(article)"
            title="标记为已读"
          />  
        </div>
        <span class="pub-date">{{ article.displayPubDate || dateUtils.formatDate(article.pubDate) }}</span>  
      </li>
    </ul>

    <!-- Pagination Controls -->
    <div class="pagination-controls">
      <button @click="changePage(feedStore.currentPage - 1)" :disabled="feedStore.currentPage === 1">上一页</button>
      <span>第 {{ feedStore.currentPage }} 页，共 {{ totalPages }} 页</span>
      <button @click="changePage(feedStore.currentPage + 1)" :disabled="feedStore.currentPage === totalPages">下一页</button>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
import { computed, watch, nextTick, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useFeedStore } from '@/stores/feedStore';
import { useAuthStore } from '@/stores/userStore';
import { useThemeStore } from '@/stores/themeStore';
import * as apiService from '@/services/apiService.js';

const props = defineProps({
    height: { type: Number, default: 500 },
    width: { type: Number, default: null },
    layoutMode: { type: String, default: 'vertical' },
    currentArticleId: { type: String, default: null }
});

const emit = defineEmits(['select-article', 'toggle-sidebar', 'toggle-layout']);
const router = useRouter();
const feedStore = useFeedStore();
const authStore = useAuthStore();
const themeStore = useThemeStore();
const articleListRef = ref(null);

// Utils
const dateUtils = {
    formatDate(dateStr) {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleString();
    }
};

// Computeds
const totalPages = computed(() => Math.ceil(feedStore.totalArticles / feedStore.articlesPerPage));
const hasSelected = computed(() => feedStore.articles.some(a => a.isSelected));

// Methods
const changePage = (page) => {
    if (page >= 1 && page <= totalPages.value) {
        feedStore.fetchArticles(feedStore.selectedNode.id, page);
    }
};

const onArticleClick = (article) => {
    emit('select-article', article);
};

const toggleSelectAll = () => {
    const newVal = !hasSelected.value; 
    feedStore.articles.forEach(a => a.isSelected = newVal);
};

const toggleSelectArticle = (article) => {
    article.isSelected = !article.isSelected;
};

// ... Actions (Bookmark, Export, MarkRead) ... 
const toggleBookmark = async (article) => {
    try {
        await apiService.toggleBookmark(article.id, () => {});
        article.isBookmarked = !article.isBookmarked;
    } catch (e) {
        console.error(e);
    }
};

const markAsRead = async (article) => {
     if (!authStore.isAuthenticated) return router.push('/login');
     try {
         await apiService.markAsRead(article.id, () => {}, authStore.token);
         article.isRead = true;
     } catch (e) {
         console.error(e);
     }
};

// Scroll to top when list changes
watch(() => feedStore.articles, async () => {
    await nextTick();
    if (articleListRef.value) {
        // The scrollable area is the ul.list-group inside the component, 
        // OR the container itself if overflow is set there.
        // Looking at CSS, .article-list has overflow-y: auto, but .list-group also has overflow-y: auto (line 141 in CSS)
        // Let's try resetting both to be safe, or check which one actually scrolls.
        // Based on CSS line 267, .article-list is the one flex-item with overflow-y: auto. 
        // Wait, line 141 says .list-group also has overflow-y: auto. This might be nested scrollbars.
        // Let's target the correct scroll container.
        
        // Try resetting the container
        articleListRef.value.scrollTop = 0;
        
        // Try resetting the inner list if it exists
        const listGroup = articleListRef.value.querySelector('.list-group');
        if (listGroup) listGroup.scrollTop = 0;
    }
});

// Export
const exportSelected = () => {
    const selected = feedStore.articles.filter(a => a.isSelected);
    if (selected.length === 0) return alert("请先选择文章");
    import('@/utils/exportUtils').then(mod => {
        mod.exportArticlesToCSV(selected);
    });
};

const redirectToSetup = () => {
    if (authStore.isAuthenticated) router.push('/admin');
    else router.push('/login');
};
</script>

<style scoped>
@import '@/assets/styles/AppLayout.css';

.layout-horizontal .header-actions {
  gap: 4px;
}

.layout-horizontal .action-button {
  padding: 4px 6px;
}

.layout-horizontal .action-button span:not(.action-icon) {
  display: none;
}
</style>
